import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUp, Plus, Search } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { Field, PageHeader } from "../components/PageHeader";
import { formatDate, formatNumber } from "../lib/format";

type Household = {
  id: string;
  household_code: string;
  head_name: string;
  unit_name: string;
  member_count: number | null;
  status: string;
  phone: string;
  last_delivered_at: string | null;
  delivery_count: number;
};
type Unit = { id: string; name_ar: string; type: string; status: string };

export function HouseholdsPage() {
  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const status = params.get("status") ?? "";
  const [draftSearch, setDraftSearch] = useState(search);
  const [createOpen, setCreateOpen] = useState(false);
  const { can } = useAuth();
  const query = useQuery({
    queryKey: ["households", search, status],
    queryFn: async () =>
      (
        await api<Household[]>(
          `/households?limit=100${search ? `&search=${encodeURIComponent(search)}` : ""}${status ? `&status=${status}` : ""}`
        )
      ).data
  });

  return (
    <div className="page">
      <PageHeader
        title="سجل الأسر"
        description="ابحث بالاسم أو رقم الأسرة أو الهاتف، والنتائج مقيّدة بنطاقك."
        actions={
          <>
            {can("household.import") ? (
              <Link className="button button--secondary" to="/imports">
                <FileUp aria-hidden="true" />
                استيراد ملف
              </Link>
            ) : null}
            {can("household.create") ? (
              <button className="button" type="button" onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden="true" />
                إضافة أسرة
              </button>
            ) : null}
          </>
        }
      />
      <section className="content-card filters-card" aria-label="مرشحات الأسر">
        <form
          className="filters"
          onSubmit={(event) => {
            event.preventDefault();
            setParams((current) => {
              const next = new URLSearchParams(current);
              if (draftSearch) next.set("search", draftSearch);
              else next.delete("search");
              return next;
            });
          }}
        >
          <label className="search-field">
            <span className="sr-only">بحث الأسر</span>
            <Search aria-hidden="true" />
            <input
              value={draftSearch}
              onChange={(event) => setDraftSearch(event.target.value)}
              placeholder="الاسم أو رقم الأسرة أو الهاتف"
            />
          </label>
          <label>
            <span>الحالة</span>
            <select
              value={status}
              onChange={(event) =>
                setParams((current) => {
                  const next = new URLSearchParams(current);
                  if (event.target.value) next.set("status", event.target.value);
                  else next.delete("status");
                  return next;
                })
              }
            >
              <option value="">كل الحالات</option>
              <option value="active">نشطة</option>
              <option value="suspended">موقوفة</option>
              <option value="incomplete">غير مكتملة</option>
            </select>
          </label>
          <button className="button" type="submit">
            بحث
          </button>
        </form>
      </section>
      <section className="content-card">
        {query.isPending ? (
          <LoadingState />
        ) : query.isError ? (
          <ErrorState error={query.error} retry={() => query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState title="لا توجد نتائج" description="عدّل البحث أو أضف سجل أسرة جديدًا." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>رقم الأسرة</th>
                  <th>رب الأسرة</th>
                  <th>الوحدة</th>
                  <th>الأفراد</th>
                  <th>آخر استلام</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link className="row-title" to={`/households/${row.id}`}>
                        {row.household_code}
                      </Link>
                    </td>
                    <td>
                      <strong>{row.head_name}</strong>
                      <small className="table-subline">{row.phone}</small>
                    </td>
                    <td>{row.unit_name}</td>
                    <td>{formatNumber(row.member_count)}</td>
                    <td>{formatDate(row.last_delivered_at)}</td>
                    <td>
                      <StatusBadge status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {createOpen ? <CreateHousehold onClose={() => setCreateOpen(false)} /> : null}
    </div>
  );
}

function CreateHousehold({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const units = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api<Unit[]>("/units/tree")).data
  });
  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api<{ id: string; householdCode: string; duplicateWarnings: unknown[] }>("/households", {
        method: "POST",
        body: payload
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["households"] });
      onClose();
    }
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    mutation.mutate({
      headName: data.get("headName"),
      phone: data.get("phone") || undefined,
      nationalId: data.get("nationalId") || undefined,
      memberCount: Number(data.get("memberCount")),
      unitId: data.get("unitId"),
      status: "active",
      notes: data.get("notes") || undefined
    });
  }
  return (
    <div className="dialog-layer" role="presentation">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="create-household-title">
        <div className="dialog-head">
          <div>
            <h2 id="create-household-title">إضافة أسرة</h2>
            <p>ستظهر أي إشارات تكرار من الخادم بعد الحفظ.</p>
          </div>
          <button className="button button--ghost" type="button" onClick={onClose}>
            إلغاء
          </button>
        </div>
        <form className="form-grid" onSubmit={submit}>
          <Field label="اسم رب الأسرة" htmlFor="headName">
            <input id="headName" name="headName" required minLength={2} autoFocus />
          </Field>
          <Field label="الوحدة السكنية" htmlFor="unitId">
            <select id="unitId" name="unitId" required defaultValue="">
              <option value="" disabled>
                اختر الوحدة
              </option>
              {units.data
                ?.filter((unit) => unit.status === "active")
                .map((unit) => (
                  <option value={unit.id} key={unit.id}>
                    {unit.name_ar}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="رقم الهاتف" htmlFor="phone">
            <input id="phone" name="phone" inputMode="tel" />
          </Field>
          <Field label="رقم الهوية" htmlFor="nationalId">
            <input id="nationalId" name="nationalId" inputMode="numeric" />
          </Field>
          <Field label="عدد أفراد الأسرة" htmlFor="memberCount">
            <input id="memberCount" name="memberCount" type="number" min={1} max={50} required />
          </Field>
          <Field label="ملاحظات" htmlFor="notes">
            <textarea id="notes" name="notes" rows={3} />
          </Field>
          {mutation.isError ? (
            <div className="inline-alert form-span" role="alert">
              {formatApiError(mutation.error)}
            </div>
          ) : null}
          <div className="dialog-actions form-span">
            <button className="button button--secondary" type="button" onClick={onClose}>
              إلغاء
            </button>
            <button className="button" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "جارٍ الحفظ…" : "حفظ الأسرة"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
